import React from "react";
import BaseElem from "../BaseElem";
import "../Common.css";
import "./AdlServices.css";
import { TextDropdown } from "./TextDropdown";
import { UNDEF } from "../Constants";
import { OTHERS } from "../DataConstants";
import { Div } from "./GenericView";
import { eatClick } from "Util/Utilities";

type AdlServicesProps = {
  adlList?: any[];
  requested_services?: any[];
  meal_requirements?: any[];

  className?: string;
  tdClassName?: string;
  tdInputClassName?: string;

  // disable all inputs for readonly state
  readOnly?: boolean;

  showApplyAll?: boolean;
  doneBtnText?: string;

  hidden?: boolean;
  dontAnimate?: boolean;

  style?: any;

  onAdlChange?: Function;
  title?: string;
  dontDisableDocScroll?: boolean;

  // To show Others textfield
  othersPlaceholder?: string;
  othersValue?: string;
  onOthersChange?: (ev: React.ChangeEvent<HTMLInputElement>) => void;
};

type AdlServicesState = {
  _tempAdl?: any[];
};

export class AdlServices extends BaseElem<AdlServicesProps, AdlServicesState> {
  constructor(props: AdlServicesProps) {
    super(props);
    this.state = {};
  }

  // Component maintains an internal state, call cleanup to clear
  _cleanup() {
    this.setState({
      _tempAdl: UNDEF,
    });
  }

  // returns list of Selected ADL services by combining meal_requirements as well
  _selectedAdls() {
    const props = this.props,
      requested = props.requested_services,
      mealReq = props.meal_requirements,
      list = [];

    for (let k = 0; requested && k < requested.length; k++) {
      const item = requested[k];
      if (item === "Meal Arrangement") {
        mealReq && list.push(...mealReq);
      } else {
        list.push(item);
      }
    }
    return list;
  }

  // New ADL selected from the dropdown
  _newAdlSelected = (dropdown: TextDropdown, selectedItem: any) => {
    const self = this;
    const adl = self.state._tempAdl || self._selectedAdls();
    const itemName = selectedItem.name;

    const idx = adl.findIndex((item: any) => item === itemName);
    if (idx >= 0) {
      adl.splice(idx, 1);
    } else {
      adl.push(itemName);
      if (itemName === OTHERS) {
        dropdown.async(dropdown.scrollToBottom, 200);
      }
    }

    self.setState({
      _tempAdl: adl,
    });
  };

  // cross button click on ADL Chips
  _deleteAdl(item: string) {
    const self = this,
      props = self.props;
    const mealRequirements = (props.meal_requirements || []).slice();

    let filteredAdl = (props.requested_services || []).filter(
      (adl: any) => adl !== item
    );
    let filteredMealReq = mealRequirements.filter((adl: any) => adl !== item);

    // when none of the meal requirements are selected
    // remove 'Meal Arrangement' from requested_services
    if (!filteredMealReq[0]) {
      filteredAdl = filteredAdl.filter(
        (adl: any) => adl !== "Meal Arrangement"
      );
    }

    props.onAdlChange && props.onAdlChange(filteredAdl, filteredMealReq);
  }

  // copy temporary adl list back to visit
  _onAdlPopupClose(isApplyAll = false) {
    const self = this,
      MEAL_ARR = "Meal Arrangement",
      state = self.state,
      props = self.props,
      allAdlList = props.adlList || [],
      mealArrangementList =
        (allAdlList.find((i: any) => i.name === MEAL_ARR && i.options) || {})
          .options || [];

    const services = state._tempAdl || self._selectedAdls();

    const requestedServices = [],
      mealRequirements = [];

    let haveMealArrangement = false;

    for (let i = 0; i < services.length; i++) {
      const item = services[i];
      const isMealArrangementItem = mealArrangementList.find(
        (v: any) => v.name === item
      );

      if (isMealArrangementItem && !haveMealArrangement) {
        haveMealArrangement = true;
        requestedServices.push(MEAL_ARR);
      }

      if (isMealArrangementItem) {
        mealRequirements.push(item);
      } else if (item !== MEAL_ARR) {
        requestedServices.push(item);
      }
    }

    props.onAdlChange &&
      props.onAdlChange(requestedServices, mealRequirements, isApplyAll);
    self._cleanup();
  }

  _subItemRender = (item: any, isSelected: boolean) => {
    if (item.name === OTHERS && isSelected) {
      const props = this.props;
      return (
        <input
          onClick={eatClick}
          className="adl-ser-input"
          value={props.othersValue || ""}
          placeholder={props.othersPlaceholder || "Please specify details"}
          onChange={props.onOthersChange}
        />
      );
    }

    return UNDEF;
  };

  render() {
    const self = this,
      state = self.state,
      props = self.props,
      isViewOnly = props.readOnly,
      adls = props.adlList || [];

    return (
      <div className={`${props.className} v`}>
        <Div className="h wrap" mt={-3}>
          {self._selectedAdls().map((item: string) => {
            return (
              <div
                className={"chip" + (isViewOnly ? " chip-no-cross" : "")}
                key={item}
              >
                {item === OTHERS ? "Others: " + props.othersValue : item}
                <img
                  tabIndex={0}
                  src="/close.svg"
                  alt=""
                  className="chip-delete"
                  onClick={() => self._deleteAdl(item)}
                  onKeyUp={(e: any) =>
                    e.key === "Enter" && self._deleteAdl(item)
                  }
                />
              </div>
            );
          })}
        </Div>

        {props.children}

        <TextDropdown
          hidden={props.hidden}
          className={props.tdClassName}
          inputClassName={props.tdInputClassName}
          items={adls}
          readOnly
          hideFocusBar
          multiSelection
          showSelectionTick
          popupAlignTop
          dontAnimate={props.dontAnimate}
          doneBtnText={props.doneBtnText}
          dontDisableDocScroll={props.dontDisableDocScroll}
          titleProperty="name"
          dropdownColor="#4a4a4a"
          popupWidth="433px"
          popupHeight="307px"
          selectedItem={props.title || "+ Add a service"}
          selectedItems={state._tempAdl || self._selectedAdls() || []}
          onChange={self._newAdlSelected}
          onBlur={(_: any) => self._onAdlPopupClose()}
          subItemRender={self._subItemRender}
          onApplyAll={
            !props.showApplyAll
              ? UNDEF
              : (_: any) => self._onAdlPopupClose(true)
          }
        />
      </div>
    );
  }
}
